// Shared browser/server policy. Never turn arbitrary URLs into tracked targets.
export const REFERRAL_PATTERN = /^[A-Za-z0-9_-]{22,64}$/;
export function referralCodeFromQuery(params: URLSearchParams) {
  const codes = params.getAll("ref");
  return codes.length === 1 && REFERRAL_PATTERN.test(codes[0])
    ? codes[0]
    : null;
}
export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;
export const HANDOFF_PARAM = "__fv";

export function publicPage(pathname: string) {
  if (
    pathname.length > 1000 ||
    /[%\\\u0000-\u0020]/.test(pathname.replace(/%[0-9a-f]{2}/gi, ""))
  )
    return false;
  return /^\/(en|fa)(?:\/?|\/(?:about-us|contact|solution-partner|cable101|event-form|documents\/[^/]+|newsroom(?:\/archive)?|categories(?:\/[^/]+)?|product\/(?!edit-product(?:\/|$))[^/]+|c\/[^/]+)\/?)$/.test(
    pathname,
  );
}

export function cleanCampaign(value: string | null) {
  return (
    value
      ?.replace(/[\u0000-\u001f\u007f]/g, "")
      .trim()
      .slice(0, 160) || null
  );
}

export function campaignFields(params: URLSearchParams) {
  return {
    utmSource: cleanCampaign(params.get("utm_source")),
    utmMedium: cleanCampaign(params.get("utm_medium")),
    utmCampaign: cleanCampaign(params.get("utm_campaign")),
    utmContent: cleanCampaign(params.get("utm_content")),
    utmTerm: cleanCampaign(params.get("utm_term")),
  };
}

export function referrerHostname(raw: string | null | undefined) {
  try {
    const url = new URL(raw || "");
    return /^https?:$/.test(url.protocol)
      ? url.hostname
          .toLowerCase()
          .replace(/^www\./, "")
          .slice(0, 253)
      : null;
  } catch {
    return null;
  }
}

export function sameSiteHost(left: string | null, right: string | null) {
  return Boolean(left && right && left === right);
}

export function canonicalShareUrl(raw: string, origin: string) {
  try {
    const url = new URL(raw, origin);
    if (
      url.origin !== new URL(origin).origin ||
      url.username ||
      url.password ||
      !publicPage(url.pathname)
    )
      return null;
    url.searchParams.delete("ref");
    url.searchParams.delete(HANDOFF_PARAM);
    UTM_KEYS.forEach((key) => url.searchParams.delete(key));
    return url;
  } catch {
    return null;
  }
}

export function knownSource(host: string | null) {
  if (!host) return "unknown";
  const platforms: [string, string[]][] = [
    ["telegram", ["t.me", "telegram.org"]],
    ["linkedin", ["linkedin.com", "lnkd.in"]],
    ["whatsapp", ["whatsapp.com", "wa.me"]],
    ["x", ["x.com", "twitter.com", "t.co"]],
    ["google", ["google.com", "google.co.uk"]],
    ["instagram", ["instagram.com"]],
    ["facebook", ["facebook.com", "fb.com"]],
  ];
  return (
    platforms.find(([, domains]) =>
      domains.some((domain) => host === domain || host.endsWith(`.${domain}`)),
    )?.[0] || host
  );
}

export function displayReferral(raw: string, code: string | null) {
  const url = new URL(raw);
  if (!publicPage(url.pathname)) return url;
  url.searchParams.delete(HANDOFF_PARAM);
  if (code && REFERRAL_PATTERN.test(code)) url.searchParams.set("ref", code);
  else url.searchParams.delete("ref");
  return url;
}

// Analytics URL maintenance and campaign changes do not create page views.
export function navigationKey(raw: string) {
  const url = new URL(raw);
  // Cable 101 owns its article drawer separately; focus only changes that drawer.
  if (/^\/(?:en|fa)\/cable101\/?$/.test(url.pathname))
    url.searchParams.delete("focus");
  url.hash = "";
  url.searchParams.delete("ref");
  url.searchParams.delete(HANDOFF_PARAM);
  UTM_KEYS.forEach((key) => url.searchParams.delete(key));
  url.searchParams.sort();
  return url.pathname + url.search;
}

export function classifyRequest(headers: Headers) {
  const agent = headers.get("user-agent") || "";
  if (
    /bot|crawler|spider|preview|facebookexternalhit|facebot|slack|telegram|whatsapp|linkedin|headless|lighthouse|curl|wget|python|monitor/i.test(
      agent,
    )
  )
    return "bot";
  return /Mozilla\//i.test(agent) ? "likely_human" : "unknown";
}
